import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { IconChevron } from '../icons/WizardIcons';

export type MultiSelectOption = {
  id: string;
  label: string;
};

type Props = {
  id?: string;
  options: MultiSelectOption[];
  value: string[];
  onChange: (ids: string[]) => void;
  placeholder?: string;
  disabled?: boolean;
  hasError?: boolean;
  'aria-label'?: string;
  emptyMessage?: string;
  /** When true, show all options with checkboxes and keep the menu open while selecting. */
  checkboxMenu?: boolean;
};

/** Extranet-parity multi-select used by case-request wizards. */
export default function WizardMultiSelectChips({
  id,
  options,
  value,
  onChange,
  placeholder = 'Select…',
  disabled = false,
  hasError = false,
  'aria-label': ariaLabel,
  emptyMessage = 'No data found',
  checkboxMenu = false,
}: Props) {
  const listId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);

  const optionMap = useMemo(() => new Map(options.map((o) => [o.id, o])), [options]);

  const selected = useMemo(
    () => value.map((vid) => optionMap.get(vid)).filter((o): o is MultiSelectOption => Boolean(o)),
    [value, optionMap],
  );

  const available = useMemo(
    () => (checkboxMenu ? options : options.filter((o) => !value.includes(o.id))),
    [checkboxMenu, options, value],
  );

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const remove = (optionId: string) => {
    onChange(value.filter((v) => v !== optionId));
  };

  const add = (optionId: string) => {
    if (!optionId || value.includes(optionId)) return;
    onChange([...value, optionId]);
    if (!checkboxMenu) setOpen(false);
  };

  const toggle = (optionId: string) => {
    if (!optionId) return;
    if (value.includes(optionId)) {
      onChange(value.filter((v) => v !== optionId));
    } else {
      onChange([...value, optionId]);
    }
  };

  return (
    <div
      ref={rootRef}
      id={id}
      className={`ms-chips${hasError ? ' inv' : ''}${disabled ? ' disabled' : ''}`}
    >
      <div
        className="ms-chips-inner"
        role="combobox"
        aria-expanded={open}
        aria-controls={listId}
        aria-label={ariaLabel}
        tabIndex={disabled ? -1 : 0}
        onClick={() => {
          if (!disabled) setOpen((o) => !o);
        }}
        onKeyDown={(e) => {
          if (disabled) return;
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setOpen((o) => !o);
          }
          if (e.key === 'Escape') setOpen(false);
        }}
      >
        <div className="ms-chips-tags">
          {selected.length === 0 ? <span className="ms-chips-ph">{placeholder}</span> : null}
          {selected.map((opt) => (
            <span key={opt.id} className="ms-chip">
              {opt.label}
              {!disabled ? (
                <button
                  type="button"
                  aria-label={`Remove ${opt.label}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    remove(opt.id);
                  }}
                >
                  ×
                </button>
              ) : null}
            </span>
          ))}
        </div>
        <span className="ms-chips-chev" aria-hidden>
          <IconChevron />
        </span>
      </div>

      {open && !disabled ? (
        <ul id={listId} className={`ms-chips-menu${checkboxMenu ? ' ms-chips-menu--checkbox' : ''}`} role="listbox">
          {available.length > 0 ? (
            available.map((opt) =>
              checkboxMenu ? (
                <li key={opt.id}>
                  <label className="ms-chips-check-option">
                    <input
                      type="checkbox"
                      checked={value.includes(opt.id)}
                      onChange={() => toggle(opt.id)}
                    />
                    <span>{opt.label}</span>
                  </label>
                </li>
              ) : (
                <li key={opt.id}>
                  <button type="button" role="option" onClick={() => add(opt.id)}>
                    {opt.label}
                  </button>
                </li>
              ),
            )
          ) : (
            <li className="ms-chips-empty">{emptyMessage}</li>
          )}
        </ul>
      ) : null}
    </div>
  );
}
